import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AudioLines,
  FileText,
  Folder,
  Globe,
  Image as ImageIcon,
  Loader2,
  Video,
} from 'lucide-react';
import { cn } from '@/admin/lib/utils';
import type { PublicShareItem } from '../lib/types';

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

export function KindIcon(props: {
  kind: PublicShareItem['kind'];
  className?: string;
}): React.JSX.Element {
  const cls = props.className ?? 'size-[15px]';
  const sw = 2;
  switch (props.kind) {
    case 'markdown':
      return <FileText className={cls} strokeWidth={sw} aria-hidden />;
    case 'image':
      return <ImageIcon className={cls} strokeWidth={sw} aria-hidden />;
    case 'audio':
      return <AudioLines className={cls} strokeWidth={sw} aria-hidden />;
    case 'video':
      return <Video className={cls} strokeWidth={sw} aria-hidden />;
    case 'webpage_archive':
      return <Globe className={cls} strokeWidth={sw} aria-hidden />;
    default:
      return <Folder className={cls} strokeWidth={sw} aria-hidden />;
  }
}

// ---------------------------------------------------------------------------
// Filename label
// ---------------------------------------------------------------------------

const FILENAME_MIDDLE_ELLIPSIS_STEM_MIN = 18;
const FILENAME_HEAD_CHARS = 12;
const FILENAME_TAIL_CHARS = 8;

function splitFilenameStemAndExtension(filename: string): {
  stem: string;
  ext: string;
} {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot <= 0) {
    return { stem: filename, ext: '' };
  }
  return {
    stem: filename.slice(0, lastDot),
    ext: filename.slice(lastDot),
  };
}

export function formatFilenameForSelectOption(name: string): string {
  const { stem, ext } = splitFilenameStemAndExtension(name);
  if (!ext) {
    return name;
  }
  if (stem.length <= FILENAME_MIDDLE_ELLIPSIS_STEM_MIN) {
    return name;
  }
  const head = stem.slice(0, FILENAME_HEAD_CHARS);
  const tail = stem.slice(-FILENAME_TAIL_CHARS);
  return `${head}…${tail}${ext}`;
}

export function ShareFilenameLabel(props: {
  name: string;
  className?: string;
  textClassName?: string;
}): React.JSX.Element {
  const { stem, ext } = splitFilenameStemAndExtension(props.name);

  if (!ext) {
    return (
      <span
        className={cn(
          'block min-w-0 truncate',
          props.className,
          props.textClassName
        )}
        title={props.name}
      >
        {props.name}
      </span>
    );
  }

  const flowRoot = (children: React.ReactNode) => (
    <span
      className={cn(
        'inline-flex min-w-0 max-w-full flex-nowrap items-baseline justify-start',
        props.className
      )}
      title={props.name}
    >
      {children}
    </span>
  );

  if (stem.length <= FILENAME_MIDDLE_ELLIPSIS_STEM_MIN) {
    return flowRoot(
      <>
        <span
          className={cn(
            'min-w-0 shrink overflow-hidden text-ellipsis whitespace-nowrap',
            props.textClassName
          )}
        >
          {stem}
        </span>
        <span className={cn('shrink-0 whitespace-nowrap', props.textClassName)}>
          {ext}
        </span>
      </>
    );
  }

  const head = stem.slice(0, FILENAME_HEAD_CHARS);
  const tail = stem.slice(-FILENAME_TAIL_CHARS);

  return flowRoot(
    <>
      <span
        className={cn(
          'min-w-0 shrink overflow-hidden text-ellipsis whitespace-nowrap',
          props.textClassName
        )}
      >
        {head}
      </span>
      <span
        className={cn('shrink-0 whitespace-nowrap', props.textClassName)}
        aria-hidden
      >
        …
      </span>
      <span
        className={cn(
          'min-w-0 shrink overflow-hidden text-ellipsis whitespace-nowrap',
          props.textClassName
        )}
      >
        {tail}
      </span>
      <span className={cn('shrink-0 whitespace-nowrap', props.textClassName)}>
        {ext}
      </span>
    </>
  );
}

// ---------------------------------------------------------------------------
// Time formatting
// ---------------------------------------------------------------------------

export function formatVideoTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// Progress bar (shared by audio & video players)
// ---------------------------------------------------------------------------

export function useProgressBar(
  duration: number,
  seek: (t: number) => void,
  showControls?: () => void,
  currentTime: number = 0
) {
  const barRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [dragProgress, setDragProgress] = useState(0);
  const [hovering, setHovering] = useState(false);
  const [hoverProgress, setHoverProgress] = useState(0);

  const getPosition = useCallback((clientX: number) => {
    const bar = barRef.current;
    if (!bar) return 0;
    const rect = bar.getBoundingClientRect();
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  }, []);

  const handleBarClick = useCallback(
    (e: React.MouseEvent) => {
      if (!duration) return;
      const pos = getPosition(e.clientX);
      seek(pos * duration);
    },
    [duration, getPosition, seek]
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const pos = getPosition(e.clientX);
      setDragging(true);
      setDragProgress(pos);
      showControls?.();
    },
    [getPosition, showControls]
  );

  useEffect(() => {
    if (!dragging) return;

    const handleMove = (e: PointerEvent) => {
      const pos = getPosition(e.clientX);
      setDragProgress(pos);
    };

    const handleUp = (e: PointerEvent) => {
      const pos = getPosition(e.clientX);
      if (duration) seek(pos * duration);
      setDragging(false);
    };

    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleUp);
    return () => {
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleUp);
    };
  }, [dragging, duration, getPosition, seek]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!duration) return;
      const STEP = 5;
      let next: number | null = null;
      switch (e.key) {
        case 'ArrowLeft':
          next = Math.max(0, currentTime - STEP);
          break;
        case 'ArrowRight':
          next = Math.min(duration, currentTime + STEP);
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = duration;
          break;
        default:
          return;
      }
      e.preventDefault();
      seek(next);
    },
    [duration, currentTime, seek]
  );

  const handleMouseEnter = useCallback(() => setHovering(true), []);
  const handleMouseLeave = useCallback(() => setHovering(false), []);
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      setHoverProgress(getPosition(e.clientX));
    },
    [getPosition]
  );

  return {
    barRef,
    dragging,
    dragProgress,
    hovering,
    hoverProgress,
    handleBarClick,
    handlePointerDown,
    handleKeyDown,
    handleMouseEnter,
    handleMouseLeave,
    handleMouseMove,
  };
}

// ---------------------------------------------------------------------------
// Empty / Error / Loading states
// ---------------------------------------------------------------------------

export function EmptyState(props: {
  icon: React.ReactNode;
  title: string;
  description: string;
}): React.JSX.Element {
  return (
    <div className="share-theme flex h-[100dvh] w-full items-center justify-center bg-secondary p-6">
      <div className="flex max-w-xs flex-col items-center gap-3 text-center">
        <span className="text-[#8a8a8a]">{props.icon}</span>
        <div>
          <h2 className="text-[15px] font-semibold text-[#1a1a1a]">
            {props.title}
          </h2>
          <p className="mt-1 text-[14px] leading-[1.6] text-[#6b6b6b]">
            {props.description}
          </p>
        </div>
      </div>
    </div>
  );
}

export function LoadingSkeleton(): React.JSX.Element {
  return (
    <div className="share-theme flex h-[100dvh] flex-col bg-secondary">
      <header className="flex h-12 shrink-0 items-center px-4">
        <div className="size-[20px] animate-pulse rounded bg-[#ebebeb]" />
        <div className="ml-2 h-4 w-14 animate-pulse rounded bg-[#ebebeb]" />
        <span className="mx-2 text-[#e8e8e8]">/</span>
        <div className="h-4 w-32 animate-pulse rounded bg-[#ebebeb]" />
      </header>
      <div className="flex min-h-0 flex-1">
        <aside className="hidden w-[232px] shrink-0 bg-[#fafafa] p-4 md:block lg:w-[260px]">
          <div className="mb-3 h-3 w-20 animate-pulse rounded bg-[#e8e8e8]" />
          <div className="space-y-1">
            {['la', 'lb', 'lc', 'ld'].map(id => (
              <div
                key={id}
                className="flex items-center gap-2 rounded px-2 py-1.5"
              >
                <div className="size-[15px] animate-pulse rounded bg-[#e8e8e8]" />
                <div className="h-3 w-28 animate-pulse rounded bg-[#e8e8e8]" />
              </div>
            ))}
          </div>
        </aside>
        <main className="flex min-h-0 min-w-0 flex-1 bg-[#fafafa] pl-0 pr-2 pt-2 pb-2">
          <div className="flex flex-1 items-center justify-center rounded-[16px] border border-[#ebebeb] bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.05)]">
            <Loader2
              className="size-5 animate-spin text-[#d0d0d0]"
              strokeWidth={2}
            />
          </div>
        </main>
      </div>
    </div>
  );
}
